import React, { useState, useMemo, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import RoleAPIs from "../../apiRoutes/Role";
import { GET_ROLES_QUERY_KEY, GET_PERMISSIONS_QUERY_KEY } from "../../utils/constants/queryKeys";
import ButtonLoader from "../Icons/ButtonLoader";
import { Button } from "../ui/button";
import AddRoleModal from "./Modals/AddRoleModal";

const RoleTab = () => {
    const [modal, setModal] = useState({ open: false, editData: null });
    const axiosPrivate = useAxiosPrivate();

    const [page, setPage] = useState(0);
    const LIMIT = 20;

    const { data: rolesResult, isLoading: rolesLoading } = useQuery({
        queryKey: [...GET_ROLES_QUERY_KEY, page],
        queryFn: () => RoleAPIs.list({ axiosPrivate, limit: LIMIT, offset: page * LIMIT }),
    });

    const { data: permResult, isLoading: permissionsLoading } = useQuery({
        queryKey: GET_PERMISSIONS_QUERY_KEY,
        queryFn: () => RoleAPIs.listPermissions({ axiosPrivate, limit: 100, offset: 0 }),
    });

    const roles = rolesResult?.data ?? [];
    const totalCount = rolesResult?.count ?? 0;
    const permissions = useMemo(() => permResult?.data ?? [], [permResult?.data]);

    const permissionMap = useMemo(() => {
        const map = {};
        permissions.forEach((p) => { map[p.id] = p; });
        return map;
    }, [permissions]);

    const getPermissionCodes = (permissionIds) => {
        if (!permissionIds?.length) return [];
        return permissionIds
            .map((id) => permissionMap[id]?.code)
            .filter(Boolean);
    };

    const isLoading = rolesLoading || permissionsLoading;

    useEffect(() => setPage(0), []);

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <ButtonLoader color="#5E30BF" />
                <span className="ml-2 text-primaryPurple">Loading Roles...</span>
            </div>
        );
    }

    return (
        <div className="p-6">
            <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-semibold text-gray-800">Roles</h3>
                <button
                    onClick={() => setModal({ open: true, editData: null })}
                    className="px-4 py-2 bg-primaryPurple text-white rounded-lg text-sm font-medium hover:bg-primaryPurple/90 transition-colors"
                >
                    Add Role
                </button>
            </div>

            <div className="flex flex-col min-h-[calc(100vh-200px)]">
                <div className="overflow-x-auto flex-1 min-h-0 rounded-xl border border-gray-200 bg-white">
                <table className="w-full text-left border-collapse">
                    <thead>
                        <tr className="border-b border-gray-200">
                            <th className="py-4 px-4 text-sm font-semibold text-gray-600">Role</th>
                            <th className="py-4 px-4 text-sm font-semibold text-gray-600 w-28">Protected</th>
                            <th className="py-4 px-4 text-sm font-semibold text-gray-600">Permissions</th>
                            <th className="py-4 px-4 text-sm font-semibold text-gray-600 w-24 text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {roles.length > 0 ? (
                            roles.map((role) => {
                                const codes = getPermissionCodes(role.permission_ids);
                                return (
                                    <tr key={role.id} className="hover:bg-gray-50/50 transition-colors">
                                        <td className="py-4 px-4 text-sm font-medium text-gray-900 capitalize">
                                            {role.name.replace(/_/g, " ")}
                                        </td>
                                        <td className="py-4 px-4">
                                            {role.is_protected ? (
                                                <span className="text-[10px] bg-blue-100 text-blue-700 px-2 py-1 rounded-md font-semibold uppercase tracking-wider">
                                                    Protected
                                                </span>
                                            ) : (
                                                <span className="text-gray-400 text-sm">—</span>
                                            )}
                                        </td>
                                        <td className="py-4 px-4">
                                            <div className="flex flex-wrap gap-1.5">
                                                {codes.length > 0 ? (
                                                    codes.map((code) => (
                                                        <span
                                                            key={code}
                                                            className="text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded border border-gray-200"
                                                        >
                                                            {code}
                                                        </span>
                                                    ))
                                                ) : (
                                                    <span className="text-gray-400 text-sm">None</span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="py-4 px-4 text-right">
                                            <button
                                                onClick={() => setModal({ open: true, editData: role })}
                                                className="text-sm text-primaryPurple font-medium hover:underline"
                                            >
                                                Edit
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })
                        ) : (
                            <tr>
                                <td colSpan="4" className="py-12 text-center text-gray-400 italic">
                                    No roles found.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
                </div>
            <div className="flex items-center justify-between px-4 py-2.5 border-t border-border bg-muted/30 flex-shrink-0">
                <span className="text-sm text-muted-foreground">
                    Showing {roles.length ? page * LIMIT + 1 : 0}–{Math.min(page * LIMIT + roles.length, totalCount)} of {totalCount}
                </span>
                <div className="flex gap-1.5">
                    <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage((p) => p - 1)} className="h-8">
                        Previous
                    </Button>
                    <Button variant="outline" size="sm" disabled={page * LIMIT + roles.length >= totalCount} onClick={() => setPage((p) => p + 1)} className="h-8">
                        Next
                    </Button>
                </div>
            </div>
            </div>

            {modal.open && (
                <AddRoleModal
                    open={modal.open}
                    onClose={() => setModal({ open: false, editData: null })}
                    editData={modal.editData}
                />
            )}
        </div>
    );
};

export default RoleTab;
